/**
 * The one normalisation routine both the name tier and the address tier run
 * through (piece 10, Duplicate detection). It is shared rather than copied so
 * the two tiers are reproducible: case folded, accents folded, punctuation
 * dropped, whitespace collapsed, and for a company name the legal suffix
 * stripped. 🔨 Address text and matching are Unicode throughout with accent
 * folding, so Nuevo León and Leon are not two different customers.
 */

/** Combining marks left behind by the decomposition, and the punctuation a name may carry. */
const COMBINING = /\p{M}+/gu;
const PUNCTUATION = /[\p{P}\p{S}]+/gu;
const WHITESPACE = /\s+/gu;

/**
 * The legal suffixes stripped from a company name before it is compared. The
 * list is ordered longest first so "Corporation" is taken before "Corp".
 */
export const LEGAL_SUFFIXES: readonly string[] = [
  'incorporated', 'corporation', 'limited liability company', 'limited', 'company',
  'llc', 'lllp', 'pllc', 'llp', 'inc', 'corp', 'ltd', 'plc', 'gmbh', 'ag', 'bv', 'nv', 'sarl', 'sas', 'spa', 'pty', 'lp', 'pc', 'co',
];

/** Decompose, drop the marks, recompose: León and Leon fold to one string. */
export function foldAccents(value: string): string {
  return value.normalize('NFD').replace(COMBINING, '').normalize('NFC');
}

/** Case folded, accents folded, punctuation dropped, whitespace collapsed. */
export function normaliseText(value: string): string {
  return foldAccents(value).toLowerCase().replace(PUNCTUATION, ' ').replace(WHITESPACE, ' ').trim();
}

/**
 * The words a company name drops wherever they stand (decision 1880): "and",
 * so Diamond Wire and Spring and Diamond Wire Spring Company fold to one name.
 * An ampersand is punctuation and is already dropped, so "&" and "and" agree.
 */
export const NAME_JOINER_WORDS: readonly string[] = ['and'];

/**
 * The same routine, the joining words dropped, then the legal suffix stripped
 * from the end, repeatedly. A name made of nothing but joining words keeps them.
 */
export function normaliseCompanyName(value: string): string {
  const folded = normaliseText(value);
  let text = folded.split(' ').filter((word) => word && !NAME_JOINER_WORDS.includes(word)).join(' ') || folded;
  for (let stripped = true; stripped; ) {
    stripped = false;
    for (const suffix of LEGAL_SUFFIXES) {
      if (text.endsWith(` ${suffix}`)) {
        text = text.slice(0, -(suffix.length + 1)).trim();
        stripped = true;
        break;
      }
    }
  }
  return text;
}

/**
 * The address tier's comparand: the lines, the locality, the area and the
 * postal code folded together, so a suite typed two ways still matches.
 */
export function normaliseAddress(parts: readonly (string | null | undefined)[]): string {
  return normaliseText(parts.filter((p): p is string => Boolean(p && p.trim())).join(' '));
}

/**
 * The street words a typed line folds away, so "100 Main St" and "100 Main
 * Street" name one building. The abbreviation and the word both map to the
 * word, which makes the fold order-free.
 */
export const STREET_WORDS: Readonly<Record<string, string>> = {
  st: 'street', str: 'street', street: 'street', rd: 'road', road: 'road', ave: 'avenue', av: 'avenue', avenue: 'avenue',
  blvd: 'boulevard', boulevard: 'boulevard', dr: 'drive', drive: 'drive', ln: 'lane', lane: 'lane', ct: 'court', court: 'court',
  pl: 'place', place: 'place', pkwy: 'parkway', pky: 'parkway', parkway: 'parkway', hwy: 'highway', highway: 'highway',
  cir: 'circle', circle: 'circle', ter: 'terrace', terrace: 'terrace', trl: 'trail', trail: 'trail', sq: 'square', square: 'square',
  n: 'north', north: 'north', s: 'south', south: 'south', e: 'east', east: 'east', w: 'west', west: 'west',
  ne: 'northeast', nw: 'northwest', se: 'southeast', sw: 'southwest',
  ste: 'suite', suite: 'suite', apt: 'apartment', apartment: 'apartment', unit: 'unit', bldg: 'building', building: 'building',
  fl: 'floor', flr: 'floor', floor: 'floor', rm: 'room', room: 'room', no: 'number', num: 'number',
  // Decision 0406's own street types, folded here rather than in a second table: the
  // verifier's "a provider that answers Lane where a person typed Ln standardised
  // nothing" and the duplicate tier's "one building, one address" are one question.
  ctr: 'center', center: 'center', expy: 'expressway', expressway: 'expressway', plz: 'plaza', plaza: 'plaza',
  rte: 'route', route: 'route', tpke: 'turnpike', turnpike: 'turnpike',
};

/**
 * One typed street line as the address tier compares it: case, punctuation and
 * spacing folded, then each street word taken to its long form. 🔨 The stored
 * value is never normalised — what the person typed is what the document
 * prints, and normalisation is for comparison only.
 */
export function normaliseStreetLine(line: string): string {
  return normaliseText(line)
    .split(' ')
    .filter(Boolean)
    .map((word) => STREET_WORDS[word] ?? word)
    .join(' ');
}

/** The parts a typed address is compared on, in the order they are folded together. */
export interface TypedAddress {
  addressLines?: readonly string[];
  locality?: string | null;
  administrativeArea?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
}

/**
 * An Unverified building's comparand: every typed line taken through the street
 * fold, then the locality, the area, the postal code and the country. It is the
 * one routine the create check and the Address Needs Review queue both read, so
 * a pair the queue lists is a pair the create would have named.
 */
export function normaliseTypedAddress(address: TypedAddress): string {
  const lines = (address.addressLines ?? []).map(normaliseStreetLine).filter(Boolean);
  // The tail folds by text alone: a state code goes through untouched, because
  // NE is Nebraska and folding it to northeast would read a state as a heading.
  const tail = [address.locality, address.administrativeArea, address.postalCode, address.countryCode]
    .map((part) => (part ? normaliseText(part) : ''))
    .filter(Boolean);
  return [...lines, ...tail].join(' ').trim();
}

/**
 * A postal code as two addresses compare it (decision 1880): a United States
 * ZIP+4 taken to its five digits, anything else folded as text.
 */
export function comparablePostalCode(value: string | null | undefined): string {
  const raw = (value ?? '').replace(/\s+/g, '').toUpperCase();
  if (/^\d{5}(-?\d{4})?$/.test(raw)) return raw.slice(0, 5);
  return normaliseText(value ?? '');
}

/**
 * The bucket two addresses at one place share (decision 1880): the first
 * street line through the street fold and the postal code. An address missing
 * either has none, so it is compared through `sameStreetAddress` alone.
 */
export function streetAddressKey(address: TypedAddress): string | null {
  const line = normaliseStreetLine(address.addressLines?.[0] ?? '');
  const postal = comparablePostalCode(address.postalCode);
  return line && postal ? `${line}|${postal}` : null;
}

/**
 * 🔒 Whether two typed addresses name one place (decision 1880): the first
 * street line through the street fold, and the postal code. The city's
 * spelling is set aside where the postal codes agree. Where either side has no
 * postal code, the cities agree instead, and a side with none matches nothing.
 * A state is compared only where both sides carry one.
 */
export function sameStreetAddress(a: TypedAddress, b: TypedAddress): boolean {
  const lineA = normaliseStreetLine(a.addressLines?.[0] ?? '');
  if (!lineA || lineA !== normaliseStreetLine(b.addressLines?.[0] ?? '')) return false;
  const stateA = normaliseText(a.administrativeArea ?? '');
  const stateB = normaliseText(b.administrativeArea ?? '');
  if (stateA && stateB && stateA !== stateB) return false;
  const postalA = comparablePostalCode(a.postalCode);
  const postalB = comparablePostalCode(b.postalCode);
  if (postalA && postalB) return postalA === postalB;
  const cityA = normaliseText(a.locality ?? '');
  return cityA !== '' && cityA === normaliseText(b.locality ?? '');
}

/** An email's domain, lower cased; anything without exactly one at-sign has none. */
export function emailDomain(address: string): string | null {
  const parts = address.trim().toLowerCase().split('@');
  return parts.length === 2 && parts[0] && parts[1] ? parts[1]! : null;
}

/** A website's host, without scheme, credentials, port, www or path. */
export function websiteDomain(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const host = withoutScheme.split('/')[0]!.split('?')[0]!.split('#')[0]!.split('@').pop()!.split(':')[0]!;
  const bare = host.startsWith('www.') ? host.slice(4) : host;
  return bare.includes('.') ? bare : null;
}

/** The digits of a phone number, with nothing else kept. */
export function phoneDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * What the phone tier compares: the national number, so a stored +15735550142
 * and a typed (573) 555-0142 are the same number. The tier is deliberately
 * loose — a missed collision creates a duplicate, which is the cheap failure —
 * and it warns rather than blocks either way.
 */
export function comparablePhone(value: string): string {
  const digits = phoneDigits(value);
  if (digits.length < 7) return '';
  return digits.length > 10 ? digits.slice(-10) : digits;
}
